import * as React from 'react';
import { initializeIcons } from '@fluentui/font-icons-mdl2';
import { Spinner } from '@fluentui/react-components';
import Cookies from 'js-cookie';
import { defineMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { useUpdateUserMutation, useUserQuery } from '@foundershub/api-client';
import { ErrorBoundary } from '../../components';
import { DrawerProvider } from '../../components/FoundersHubUserOverlayDrawer';
import AccountRequiredRedirectLayout from '../../layouts/LoggedIn';
import LoggedOutLayout from '../../layouts/LoggedOut';
import LoggedOutSimpleLayout from '../../layouts/LoggedOutSimple';
import { useCookieConsent } from '../../modules/cookie-consent';
import { useUpdateLocale } from '../../modules/intl';
import type { Locale } from '../../modules/intl/locales';
import { getLocaleHtml } from '../../modules/intl/locales';
import { MFS_REPLATFORM_FLAG, MFS_REPLATFORM_URL, useMfsReplatformLoading } from '../../modules/mfs-replatform';
import redirect from '../../modules/redirect';
import { setUser, unsetUser } from '../../modules/telemetry';
import {
  loggedInRedirectsWithFlag,
  loggedInRedirectsWithoutFlag,
  loggedInRoutesWithFlag,
  loggedInRoutesWithoutFlag,
  loggedOutRedirectsWithFlag,
  loggedOutRoutesWithFlag,
  NotFoundLoggedIn,
  NotFoundLoggedOut,
  Routes,
} from '../../routes';
import { useFeatureFlagEnabled } from '../../store';
import { bootstrap } from '..';

initializeIcons();

export function App() {
  const { data: user, isLoading, error } = useUserQuery();
  const history = useHistory();
  const [userLocaleFromQueryParam] = React.useState(history.location.search.includes('lang='));

  // remove after 06/30/2025 deploy
  const buildWithAIAndDiscover = useFeatureFlagEnabled('build-with-ai-and-discover');

  const loggedOutReds = loggedOutRedirectsWithFlag;
  const loggedOutRts = loggedOutRoutesWithFlag;

  let loggedInReds = loggedInRedirectsWithoutFlag;
  let loggedInRts = loggedInRoutesWithoutFlag;
  if (buildWithAIAndDiscover) {
    loggedInReds = loggedInRedirectsWithFlag;
    loggedInRts = loggedInRoutesWithFlag;
  }

  const location = useLocation();
  const setUserLocale = useUpdateLocale();
  const { siteConsent } = useCookieConsent();
  // Added for 1DS tracking (Only capture on initial load)
  const [mcId] = React.useState<string | null>(new URLSearchParams(window.location.search).get('WT.mc_id'));
  const intl = useIntl();
  let redirects = loggedOutReds;
  let routes = loggedOutRts;
  const notFound = user ? NotFoundLoggedIn : NotFoundLoggedOut;
  let fallback = '/login';
  let antiRoutes = loggedInRts;
  const [updateUserDetail] = useUpdateUserMutation();
  let routesComponent = <Routes notFound={notFound} redirects={redirects} routes={routes} />;
  let parentLayout = <LoggedOutLayout>{routesComponent}</LoggedOutLayout>;

  const isSignupPath = history.location.pathname.includes('/signup');
  const isApplyPath = history.location.pathname.includes('/apply');
  const isApplyExactPath = history.location.pathname === '/apply';
  const isApplySubmittedPath = history.location.pathname === '/apply/submitted';
  const isLogoutPath = history.location.pathname.includes('/logout');
  const isReferralPath = history.location.pathname.includes('/referral');
  const isRegisterPath = history.location.pathname.includes('/register');
  const isLoginPath = history.location.pathname === '/login';
  const isPortalEntryPath =
    !isApplySubmittedPath && (isSignupPath || isApplyExactPath || isReferralPath || isRegisterPath);
  const userHasAccounts = !!user && user.accounts.length > 0;

  const mfsReplatformEnabled = useFeatureFlagEnabled(MFS_REPLATFORM_FLAG);
  const mfsConfigLoading = useMfsReplatformLoading();

  // Show spinner on paths that may be affected while MFS config loads.
  // Not gated by mfsReplatformEnabled — we need to hold the render until the flag value is known.
  const isMfsAffectedPath =
    isLoginPath ||
    (!user && (isSignupPath || isApplyPath || isRegisterPath || isReferralPath || history.location.pathname === '/')) ||
    (user && isApplyPath);
  // Only redirect to portal once we know the flag is on and user loading has settled.
  const shouldMfsRedirect = mfsReplatformEnabled && !isLoading && isPortalEntryPath && (!user || !isApplyExactPath);

  React.useEffect(() => {
    if (shouldMfsRedirect) {
      window.location.href = MFS_REPLATFORM_URL;
    }
  }, [shouldMfsRedirect]);

  if (user && !isApplyPath && !isLogoutPath) {
    fallback = '/benefits';
    redirects = loggedInReds;
    routes = loggedInRts;
    antiRoutes = loggedOutRts;
    routesComponent = <Routes notFound={notFound} redirects={redirects} routes={routes} />;
    parentLayout = <AccountRequiredRedirectLayout>{routesComponent}</AccountRequiredRedirectLayout>;
  } else if (
    user &&
    (isApplyPath || isLogoutPath) &&
    (!mfsReplatformEnabled || (!isPortalEntryPath && !isApplySubmittedPath))
  ) {
    redirects = loggedInReds;
    routesComponent = <Routes notFound={notFound} redirects={redirects} routes={routes} />;
    parentLayout = <LoggedOutLayout>{routesComponent}</LoggedOutLayout>;
  } else if (mfsReplatformEnabled && user && isApplySubmittedPath) {
    redirects = [];
    routes = loggedOutRts;
    antiRoutes = [];
    routesComponent = <Routes notFound={notFound} redirects={redirects} routes={routes} />;
    parentLayout = <LoggedOutLayout>{routesComponent}</LoggedOutLayout>;
  } else if (mfsReplatformEnabled && user && userHasAccounts && isPortalEntryPath) {
    fallback = '/benefits';
    redirects = [
      { from: '/signup', to: '/' },
      { from: '/apply', to: '/' },
      { from: '/referral', to: '/' },
      { from: '/register', to: '/' },
      ...loggedInReds,
    ];
    routes = loggedInRts;
    antiRoutes = loggedOutRts;
    routesComponent = <Routes notFound={notFound} redirects={redirects} routes={routes} />;
    parentLayout = <AccountRequiredRedirectLayout>{routesComponent}</AccountRequiredRedirectLayout>;
  } else if (mfsReplatformEnabled && user && !userHasAccounts && (isApplyExactPath || isApplySubmittedPath)) {
    redirects = [];
    routes = loggedOutRts;
    antiRoutes = [];
    routesComponent = <Routes notFound={notFound} redirects={redirects} routes={routes} />;
    parentLayout = <LoggedOutLayout>{routesComponent}</LoggedOutLayout>;
  } else if (mfsReplatformEnabled && !user) {
    redirects = [
      { from: '/', to: '/login' },
      { from: '/logout', to: '/login' },
    ];
    routesComponent = <Routes notFound={notFound} redirects={redirects} routes={routes} />;
    parentLayout = <LoggedOutLayout>{routesComponent}</LoggedOutLayout>;
  }

  if (!user && (isSignupPath || isReferralPath)) {
    parentLayout = <LoggedOutSimpleLayout>{routesComponent}</LoggedOutSimpleLayout>;
  }

  React.useEffect(() => {
    if (user) {
      setUser(user);
    }
    if (error) {
      unsetUser();
    }
  }, [user && user.email, error]);

  React.useEffect(() => {
    const urlParams = new URLSearchParams(location.search);

    if (user && user.locale) {
      setUserLocale(user.locale as Locale);
    }
    if (urlParams.has('lang')) {
      const lang = getLocaleHtml(urlParams.get('lang') as Locale);
      setUserLocale(lang);
    }
    // n.b. intentionally not depending on location.search to preserve ?lang= across all routes in a session
  }, [userLocaleFromQueryParam, user && user.locale]);

  React.useEffect(() => {
    if (user) {
      const msfpc_data = Cookies.get('MSFPC');

      // MSFPC GUID has no dashes
      const msfpc_regex = /GUID=([a-z0-9]{32})/;
      const msfpc_id = !!msfpc_data && msfpc_data.match(msfpc_regex)?.at(1);
      if (user.msfpc_id == null && msfpc_id) {
        updateUserDetail({ msfpc_id });
      }
    }
  }, [user]);

  React.useEffect(() => {
    // Added for 1DS tracking
    if (siteConsent) {
      if (!siteConsent.isConsentRequired || siteConsent.getConsent().Analytics) {
        if (mcId !== null && location.pathname !== '/apply/submitted') {
          Cookies.set('mc_id', mcId, { expires: 365 });
        }
      } else {
        Cookies.remove('mc_id');
      }
    }
  }, [siteConsent?.getConsentFor('Analytics')]);

  const parameterizedRedirect = redirect(history.location.pathname, antiRoutes, redirects, fallback);

  if (parameterizedRedirect != null) {
    redirects.push(parameterizedRedirect);
  }

  return (
    <ErrorBoundary type="full">
      <DrawerProvider>
        {isLoading && !error && !user ? (
          <Spinner label={intl.formatMessage(loadingLabel)} labelPosition="below" />
        ) : (mfsConfigLoading && isMfsAffectedPath) || shouldMfsRedirect ? (
          <Spinner label={intl.formatMessage(loadingLabel)} labelPosition="below" />
        ) : (
          <div>{parentLayout}</div>
        )}
      </DrawerProvider>
    </ErrorBoundary>
  );
}

const loadingLabel = defineMessage({ defaultMessage: 'Loading user info', description: 'Label for loading spinner' });

bootstrap(App);
